import * as React from "react"

import { ReasonDialog } from "@/components/ReasonDialog"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { t } from "@/lib/i18n"
import type { Person } from "@/lib/types"

/**
 * DAR DE BAJA A UNA PERSONA: fecha de salida, motivo y su documentación.
 *
 * Cesar, 2026-10-04: «deberíamos poder darle de baja a una persona también y
 * poner la fecha de cierre y su documentación de salida».
 *
 * No se borra nada: la persona sigue en el maestro con su fecha de salida, y
 * desde esa fecha deja de contar en la planilla y en las obligaciones. El
 * documento de salida (liquidación, carta, finiquito) se archiva en el
 * Expediente de la sociedad con el nombre de la persona.
 */
export function BajaDePersonaDialog({
  persona, onOpenChange, pending, onConfirm,
}: {
  persona: Person | null
  onOpenChange: (v: boolean) => void
  pending: boolean
  onConfirm: (datos: { fechaSalida: string; motivo: string; archivo: File | null }) => void | Promise<void>
}) {
  const [fecha, setFecha] = React.useState(() => new Date().toISOString().slice(0, 10))
  const [archivo, setArchivo] = React.useState<File | null>(null)

  React.useEffect(() => {
    if (persona) {
      setFecha(new Date().toISOString().slice(0, 10))
      setArchivo(null)
    }
  }, [persona])

  return (
    <ReasonDialog
      open={Boolean(persona)}
      onOpenChange={onOpenChange}
      title={`${t("Dar de baja")} · ${persona?.fullName ?? ""}`}
      description={t("No se borra: queda con su fecha de salida y desde ahí deja de contar en la planilla y en las obligaciones. Si adjuntas el documento de salida, se archiva en el Expediente.")}
      confirmLabel={t("Dar de baja")}
      variant="destructive"
      pending={pending}
      suggestions={["Renuncia voluntaria.", "Fin del contrato.", "Despido con liquidación pagada."]}
      onConfirm={(motivo) => onConfirm({ fechaSalida: fecha, motivo, archivo })}
      summary={
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="baja-fecha">{t("Fecha de salida")}</Label>
            <SelectorDeFecha id="baja-fecha" value={fecha} onChange={setFecha} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="baja-doc">{t("Documento de salida (opcional)")}</Label>
            <Input id="baja-doc" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                   onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
            <p className="text-2xs text-muted-foreground">{t("Liquidación, carta o finiquito. PDF o imagen.")}</p>
          </div>
        </div>
      }
    />
  )
}
